/**
 * Runtime-portable embedded SQLite for the logging sink.
 *
 * The log store needs a synchronous, embedded SQLite driver. Bun ships
 * `bun:sqlite`; Node 22.5+ ships `node:sqlite`. This shim selects the right
 * one at runtime so the CLI and runtime run unmodified under either — no Bun
 * dependency baked into the published build.
 *
 * Both drivers accept `$`-prefixed named-parameter keys directly, so callers
 * use one binding convention. The only API gap is transactions: `bun:sqlite`
 * has `db.transaction()`, `node:sqlite` does not — {@link openSqlite}
 * synthesizes it with manual `BEGIN`/`COMMIT`/`ROLLBACK`.
 */
import { createRequire } from "node:module";

const nodeRequire = createRequire(import.meta.url);

/** True when executing under the Bun runtime. */
const isBun = typeof (globalThis as { Bun?: unknown }).Bun !== "undefined";

/** A prepared statement bound with `$`-prefixed named parameters. */
export interface PortableStatement {
  run(params?: Record<string, unknown>): void;
  all<T = unknown>(params?: Record<string, unknown>): T[];
  get<T = unknown>(params?: Record<string, unknown>): T | undefined;
}

/** A synchronous embedded SQLite handle, uniform across Bun and Node. */
export interface PortableDatabase {
  exec(sql: string): void;
  prepare(sql: string): PortableStatement;
  /** Wrap `fn` so the statements it runs commit as one transaction. */
  transaction<A extends unknown[]>(fn: (...args: A) => void): (...args: A) => void;
  close(): void;
}

export interface OpenSqliteOptions {
  /** Open read-only. The database file must already exist. */
  readonly?: boolean;
}

/** Minimal structural shape shared by `bun:sqlite` and `node:sqlite` handles. */
interface RawStatement {
  run(params?: unknown): unknown;
  all(params?: unknown): unknown[];
  get(params?: unknown): unknown;
}
interface RawDatabase {
  exec(sql: string): unknown;
  prepare(sql: string): RawStatement;
  close(): void;
}

function wrapStatement(stmt: RawStatement): PortableStatement {
  return {
    run: (params) => {
      if (params === undefined) stmt.run();
      else stmt.run(params);
    },
    all: (params) => (params === undefined ? stmt.all() : stmt.all(params)) as never,
    get: (params) => (params === undefined ? stmt.get() : stmt.get(params)) as never,
  };
}

function openBunSqlite(path: string, opts: OpenSqliteOptions): PortableDatabase {
  const { Database } = nodeRequire("bun:sqlite") as {
    Database: new (
      p: string,
      o?: { readonly?: boolean },
    ) => RawDatabase & {
      transaction<A extends unknown[]>(fn: (...args: A) => void): (...args: A) => void;
    };
  };
  const db = opts.readonly ? new Database(path, { readonly: true }) : new Database(path);
  return {
    exec: (sql) => {
      db.exec(sql);
    },
    prepare: (sql) => wrapStatement(db.prepare(sql)),
    transaction: (fn) => db.transaction(fn),
    close: () => {
      db.close();
    },
  };
}

function openNodeSqlite(path: string, opts: OpenSqliteOptions): PortableDatabase {
  const { DatabaseSync } = nodeRequire("node:sqlite") as {
    DatabaseSync: new (p: string, o?: { readOnly?: boolean }) => RawDatabase;
  };
  const db = opts.readonly ? new DatabaseSync(path, { readOnly: true }) : new DatabaseSync(path);
  return {
    exec: (sql) => {
      db.exec(sql);
    },
    prepare: (sql) => wrapStatement(db.prepare(sql)),
    transaction:
      (fn) =>
      (...args) => {
        db.exec("BEGIN");
        try {
          fn(...args);
          db.exec("COMMIT");
        } catch (err) {
          db.exec("ROLLBACK");
          throw err;
        }
      },
    close: () => {
      db.close();
    },
  };
}

/**
 * Open an embedded SQLite database using the runtime's native driver —
 * `bun:sqlite` under Bun, `node:sqlite` under Node 22.5+.
 */
export function openSqlite(path: string, opts: OpenSqliteOptions = {}): PortableDatabase {
  return isBun ? openBunSqlite(path, opts) : openNodeSqlite(path, opts);
}
